/* global grecaptcha */
/**  grecaptcha is assigned to the global window variable by the script
 * in the loadRecaptcha function
 */
import config from '@epi-config';
const scriptId = 'reCaptchaScript';

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (key: string, options: { action: string }) => Promise<string>;
    };
  }
}

export const RECAPTCHA_ERROR_NAME = 'RecaptchaError';

export class RecaptchaError extends Error {
  translationKey = 'validation_messages.username_availability_recaptcha';

  constructor(message: string) {
    super(message);
    this.name = RECAPTCHA_ERROR_NAME;
  }
}

const getScript = () => document.getElementById(scriptId);

const isScriptLoaded = () =>
  getScript()?.classList.contains('isLoaded') ?? false;

const waitForGrecaptchaReady = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const recaptcha = window.grecaptcha;

    if (!recaptcha?.ready) {
      reject(new RecaptchaError('reCAPTCHA is not loaded'));
      return;
    }

    try {
      recaptcha.ready(() => resolve());
    } catch (error) {
      reject(
        error instanceof RecaptchaError
          ? error
          : new RecaptchaError('reCAPTCHA is not loaded')
      );
    }
  });

const loadRecaptcha = (key: string): Promise<void> =>
  new Promise((resolve, reject) => {
    const existingScript = getScript();

    if (existingScript) {
      if (isScriptLoaded()) {
        waitForGrecaptchaReady().then(resolve).catch(reject);
        return;
      }

      existingScript.addEventListener('load', () => {
        existingScript.classList.add('isLoaded');
        waitForGrecaptchaReady().then(resolve).catch(reject);
      });
      existingScript.addEventListener('error', () => {
        reject(new RecaptchaError('reCAPTCHA failed to load'));
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${key}`;
    script.id = scriptId;
    script.onload = () => {
      script.classList.add('isLoaded');
      waitForGrecaptchaReady().then(resolve).catch(reject);
    };
    script.onerror = () => {
      reject(new RecaptchaError('reCAPTCHA failed to load'));
    };
    document.body.appendChild(script);
  });

export const loadRecaptchaScript = (key: string): Promise<void> => {
  if (config.disableReCaptcha) {
    return Promise.resolve();
  }

  if (!key) {
    return Promise.reject(new RecaptchaError('reCAPTCHA site key is missing'));
  }

  if (isScriptLoaded() && window.grecaptcha?.ready) {
    return waitForGrecaptchaReady();
  }

  return loadRecaptcha(key);
};

const toRecaptchaError = (error: unknown, fallbackMessage: string) => {
  const errorMessage = error instanceof Error ? error.message : fallbackMessage;
  return error instanceof RecaptchaError
    ? error
    : new RecaptchaError(errorMessage);
};

export const getRecaptchaToken = (
  key: string,
  action: string
): Promise<string> => {
  if (config.disableReCaptcha) {
    return Promise.resolve('');
  }

  if (!key) {
    return Promise.reject(new RecaptchaError('reCAPTCHA site key is missing'));
  }

  const recaptcha = window.grecaptcha;

  if (!recaptcha?.ready || !recaptcha?.execute) {
    return Promise.reject(new RecaptchaError('reCAPTCHA is not loaded'));
  }

  return new Promise((resolve, reject) => {
    try {
      recaptcha.ready(() => {
        recaptcha
          .execute(`${key}`, {
            action
          })
          .then(resolve)
          .catch(error =>
            reject(toRecaptchaError(error, 'reCAPTCHA execute failed'))
          );
      });
    } catch (error) {
      reject(toRecaptchaError(error, 'reCAPTCHA is not loaded'));
    }
  });
};
